<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="packingListsFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            fixed-header
            item-key="id"
            :items-per-page.sync="itemsPerPage"
            :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="6" class="d-flex mb-n3">
                        <h1 class="mr-4">PACKING LISTS</h1>
                    </v-col>
                    <v-spacer />
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.code`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="search"
                    dense
                />
            </template>
            <template v-slot:[`header.workOrdersAndBoms`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="workOrderAndBomToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.createdBy`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="userToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.createdOn`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="createdOnToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.requestedShipmentDate`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="requestedShipmentDateToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.notes`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="notesToFilter"
                    dense
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.createdByName }}</p>
                </div>
            </template>
            <template v-slot:[`item.workOrdersAndBoms`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.selectedWorkOrdersAndBomsString }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.projectLabel }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.createdOn`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.createdOnLabel }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.requestedShipmentDate`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.requestedShipmentDate }}
                    </p>
                </div>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openProcessPackingList(item)">
                        <v-icon>{{
                            item.status !== 'completed'
                                ? 'mdi-pencil'
                                : 'mdi-eye'
                        }}</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <!--PROCESS PACKING ORDER-->
        <v-dialog
            v-model="processPackingList"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <ProcessPackingPending
                v-if="processPackingList && selectedOrder.status == 'pending'"
                :originalOrder="selectedOrder"
                :project="selectedProject"
                @replacePackingList="replacePackingList"
                @close="closeProcessPackingList"
            />
        </v-dialog>
    </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import API from '@/services/api'
import { mapMutations } from 'vuex'
import {
    FILTER_DEBOUNCE_MS,
    decoratePackingList,
    indexById,
    normalizeFilter,
    projectRecord,
} from '@/utils/packingListBoard'

export default {
    name: 'PendingPacking',
    components: {
        ProcessPackingPending: () =>
            import('@/components/Packing/ProcessPackingPending.vue'),
    },
    props: {
        project: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        projects: [],
        selectedProject: {},
        selectedOrder: undefined,
        processPackingList: false,
        projectToFilter: undefined,
        workOrderAndBomToFilter: undefined,
        requestedShipmentDateToFilter: undefined,
        createdOnToFilter: undefined,
        userToFilter: undefined,
        notesToFilter: undefined,
        loading: false,
        activateAlert: false,
        alertMessage: undefined,
        search: null,
        headers: [
            {
                text: 'ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
            },
            {
                text: 'WOS - BOMS',
                value: 'workOrdersAndBoms',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CREATED BY',
                value: 'createdBy',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CREATED ON',
                value: 'createdOn',
                align: 'center',
                sortable: false,
            },
            {
                text: 'REQUESTED SHIPMENT DATE',
                value: 'requestedShipmentDate',
                align: 'center',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'center',
                sortable: false,
            },
            {
                text: 'NET MASS',
                value: 'netMass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'GROSS MASS',
                value: 'grossMass',
                align: 'right',
                sortable: false,
            },

            {
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
            },
        ],
        statusColor: {
            inProgress: '#9E9FA0',
            pending: '#FF9800',
            referred: 'primary',
            scheduled: '#25539d',
            rejected: 'red',
            completed: 'black',
            shipped: '#27BC4F',
        },
        packingLists: [],
        users: [],
        usersById: {},
        projectsById: {},
        itemsPerPage: 50,
        appliedFilters: {
            search: '',
            workOrderAndBom: '',
            user: '',
            createdOn: '',
            requestedShipmentDate: '',
            project: '',
            notes: '',
        },
        statusToFilter: 'pending',
    }),
    computed: {
        packingListsFiltered() {
            let conditions = []
            if (this.appliedFilters.search) {
                conditions.push(this.filterOrder)
            }
            if (this.appliedFilters.workOrderAndBom) {
                conditions.push(this.filterWorkOrder)
            }
            if (this.appliedFilters.project) {
                conditions.push(this.filterProject)
            }

            if (this.appliedFilters.user) {
                conditions.push(this.filterByUser)
            }
            if (this.statusToFilter) {
                conditions.push(this.filterByStatus)
            }
            if (this.appliedFilters.notes) {
                conditions.push(this.filterByNotes)
            }
            if (this.appliedFilters.requestedShipmentDate) {
                conditions.push(this.filterByRequestedShipmentDate)
            }
            if (this.appliedFilters.createdOn) {
                conditions.push(this.filterByCreatedOn)
            }
            return this.packingLists.filter(order =>
                conditions.every(condition => condition(order))
            )
        },
    },
    watch: {
        search: 'queueFilters',
        workOrderAndBomToFilter: 'queueFilters',
        userToFilter: 'queueFilters',
        createdOnToFilter: 'queueFilters',
        requestedShipmentDateToFilter: 'queueFilters',
        projectToFilter: 'queueFilters',
        notesToFilter: 'queueFilters',
    },
    async mounted() {
        try {
            this.loading = true
            const [
                userResponse,
                usersResponse,
                projects,
                packingLists,
            ] = await Promise.all([
                API.getMyInfo(),
                API.getLiteUsers(),
                API.getLiteProjectsByUser(),
                API.getPackingLists({ status: 'pending' }),
            ])
            this.user = userResponse.data.user
            this.users = usersResponse.data.users
            this.usersById = indexById(this.users)
            this.projects = projects
            this.projectsById = indexById(this.projects)
            this.packingLists = packingLists
            this.packingLists.forEach(pl => {
                this.decorateRow(pl)
            })
            this.sort()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        clearTimeout(this._filterTimer)
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        decorateRow(packingList) {
            decoratePackingList(packingList, this.usersById, this.projectsById)
        },
        replacePackingList(packingList) {
            const index = this.packingLists.findIndex(
                p => p.id == packingList.id
            )
            if (index >= 0) {
                this.packingLists.splice(index, 1, {
                    ...this.packingLists[index],
                    ...packingList,
                })
                this.decorateRow(this.packingLists[index])
            }
        },
        openProcessPackingList(item) {
            this.selectedOrder = cloneDeep(item)
            this.selectedProject = cloneDeep(this.findProject(item.projectId))
            this.processPackingList = true
        },
        closeProcessPackingList() {
            this.selectedOrder = null
            this.selectedProject = null
            this.processPackingList = false
        },
        filterOrder(order) {
            return order.code
                .toString()
                .toLowerCase()
                .includes(this.appliedFilters.search)
        },
        filterByUser(order) {
            return (order.createdByName || '')
                .toLowerCase()
                .includes(this.appliedFilters.user)
        },
        filterWorkOrder(order) {
            return order.selectedWorkOrdersAndBomsString
                .toLowerCase()
                .includes(this.appliedFilters.workOrderAndBom)
        },
        filterProject(order) {
            return (order.projectLabel || '')
                .toLowerCase()
                .includes(this.appliedFilters.project)
        },
        filterByStatus(order) {
            return order.status
                .toString()
                .toLowerCase()
                .includes(this.statusToFilter.toLowerCase())
        },
        filterByNotes(order) {
            if (order.notes) {
                return order.notes
                    .toLowerCase()
                    .includes(this.appliedFilters.notes)
            } else {
                return false
            }
        },
        filterByCreatedOn(order) {
            return (order.createdOnLabel || '')
                .toLowerCase()
                .includes(this.appliedFilters.createdOn)
        },
        filterByRequestedShipmentDate(order) {
            return (
                order.requestedShipmentDate &&
                order.requestedShipmentDate
                    .toLowerCase()
                    .includes(this.appliedFilters.requestedShipmentDate)
            )
        },
        sort() {
            this.packingLists.sort(
                (a, b) =>
                    (b.createdOn.seconds || b.createdOn._seconds) -
                    (a.createdOn.seconds || a.createdOn._seconds)
            )
        },
        findProject(id) {
            return projectRecord(this.projectsById, id)
        },
        queueFilters() {
            clearTimeout(this._filterTimer)
            this._filterTimer = setTimeout(() => {
                this.appliedFilters = {
                    search: normalizeFilter(this.search),
                    workOrderAndBom: normalizeFilter(
                        this.workOrderAndBomToFilter
                    ),
                    user: normalizeFilter(this.userToFilter),
                    createdOn: normalizeFilter(this.createdOnToFilter),
                    requestedShipmentDate: normalizeFilter(
                        this.requestedShipmentDateToFilter
                    ),
                    project: normalizeFilter(this.projectToFilter),
                    notes: normalizeFilter(this.notesToFilter),
                }
            }, FILTER_DEBOUNCE_MS)
        },
    },
}
</script>

<style>
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
.v-data-table__wrapper {
    max-height: 72vh;
    overflow-y: auto;
}
.v-data-table__row {
    height: 64px;
}
.v-data-table__wrapper thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
}
thead {
    position: sticky;
    z-index: 1;
}
</style>
